import React, { useRef, useState } from 'react';

import { ColorPicker, ValueBox, ValueGroup, ValueStepper } from '@candu-ai/craft/ui';
import { Spacing } from 'craft/containers/Toolbox/sections/Spacing';
import { getCssResetStyleMessage, StyleHierarchy, useCss } from 'craft/hooks';
import { CssSelectorId } from 'craft/types';

interface Props {
  nodeIds: string[];
  dismissCssSelector: CssSelectorId;
  dismissSVGCssSelector: CssSelectorId;
  defaultValues?: {
    [CssSelectorId.Dismiss]?: Record<string, any>;
    [CssSelectorId.DismissSVG]?: Record<string, any>;
  };
}

export const DismissIconSettings = ({
  nodeIds,
  dismissCssSelector,
  dismissSVGCssSelector,
  defaultValues,
}: Props) => {
  const { css, setCss, stylesHierarchy } = useCss(nodeIds, {
    cssSelectorId: dismissSVGCssSelector,
  });

  const defaultStroke =
    stylesHierarchy?.stroke?.fallbackValue ||
    defaultValues?.[CssSelectorId.DismissSVG]?.stroke ||
    '#7e86a0';
  const stroke = css?.stroke || defaultStroke;

  const defaultStrokeWidth = Number.isNaN(
    parseFloat(
      stylesHierarchy?.strokeWidth?.fallbackValue ||
        defaultValues?.[CssSelectorId.DismissSVG]?.strokeWidth ||
        '',
    ),
  )
    ? 1
    : parseFloat(
        stylesHierarchy?.strokeWidth?.fallbackValue ||
          defaultValues?.[CssSelectorId.DismissSVG]?.strokeWidth ||
          '1',
      );
  const strokeWidth = css?.strokeWidth ? parseFloat(css.strokeWidth) : defaultStrokeWidth;

  const strokeWidthRef = useRef<HTMLInputElement>();
  const [inputValue, setInputValue] = useState<number | undefined>(strokeWidth);

  return (
    <>
      <ValueGroup>
        <ColorPicker
          label="Icon Color"
          isInGroup
          onResetTooltip={getCssResetStyleMessage(StyleHierarchy.Styleguide)}
          defaultValue={defaultStroke as string}
          value={stroke}
          onChange={(newColor) => {
            setCss({ stroke: newColor });
          }}
        />
        <ValueBox label="Stroke Width" isInGroup={{ isLast: true }}>
          <ValueStepper
            ref={strokeWidthRef}
            step={1}
            name="hotspot-stroke-width"
            placeholder={typeof inputValue === 'undefined' ? undefined : 'unset'}
            valueSuffix="px"
            editable
            format="inline"
            maxValue={10}
            minValue={1}
            onChange={(newValue?: number) => {
              setInputValue(newValue);
              // only trigger change when there is no value or the value is within the accepted range
              if (typeof newValue === 'undefined' || newValue >= 1 || newValue <= 10) {
                setCss({ strokeWidth: newValue });
              }
            }}
            onBlur={() => {
              if (typeof inputValue === 'undefined') {
                // if the value was deleted at the time of blurring, reflect actual value in the input
                setInputValue(strokeWidth);
              }
            }}
            value={inputValue}
            onResetTooltip={getCssResetStyleMessage(StyleHierarchy.Styleguide)}
            onReset={
              typeof inputValue !== 'undefined' && inputValue !== defaultStrokeWidth
                ? () => {
                    setCss({ strokeWidth: undefined });
                    setInputValue(defaultStrokeWidth);
                  }
                : undefined
            }
          />
        </ValueBox>
      </ValueGroup>
      <ValueGroup>
        <Spacing
          nodeIds={nodeIds}
          schema={{
            cssSelectorId: dismissCssSelector,
            hasMargin: false,
            hasPadding: true,
          }}
          cssDefaults={defaultValues?.[CssSelectorId.Dismiss]}
        />
      </ValueGroup>
    </>
  );
};
